import React from 'react';
import { Box, Chip, LinearProgress, Typography } from '@mui/material';
import { Warning, Error, CheckCircle } from '@mui/icons-material';
import { lifeCycleService, LifeCycleAlert } from '../services/lifeCycle.service';

interface LifeCycleIndicatorProps {
  currentShots: number;
  designLife: number;
  size?: 'small' | 'medium' | 'large';
  showDetails?: boolean;
}

const LifeCycleIndicator: React.FC<LifeCycleIndicatorProps> = ({
  currentShots,
  designLife,
  size = 'medium',
  showDetails = true
}) => {
  const status: LifeCycleAlert = lifeCycleService.evaluateLifeStatus(currentShots, designLife);
  const remainingShots = lifeCycleService.calculateRemainingShots(currentShots, designLife);

  const getProgressColor = (level: string) => {
    switch (level) {
      case 'danger': return 'error';
      case 'warning': return 'warning';
      default: return 'success';
    }
  };

  const getStatusIcon = (level: string) => {
    switch (level) {
      case 'danger': return <Error color="error" />;
      case 'warning': return <Warning color="warning" />;
      default: return <CheckCircle color="success" />;
    }
  };

  const getChipColor = (level: string) => {
    switch (level) {
      case 'danger': return 'error' as const;
      case 'warning': return 'warning' as const;
      default: return 'success' as const;
    }
  };

  return (
    <Box>
      {/* 진행률 표시 */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {size !== 'small' && getStatusIcon(status.level)}
        <Box sx={{ flexGrow: 1, mx: 1 }}>
          <LinearProgress
            variant="determinate"
            value={status.percentage}
            color={getProgressColor(status.level)}
            sx={{ height: size === 'large' ? 10 : size === 'medium' ? 8 : 6 }}
          />
        </Box>
        <Typography variant={size === 'large' ? 'body1' : 'body2'} fontWeight="bold">
          {status.percentage}%
        </Typography>
      </Box>

      {/* 상태 칩 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: showDetails ? 1 : 0 }}>
        <Chip
          label={status.message}
          color={getChipColor(status.level)}
          size={size === 'large' ? 'medium' : 'small'}
          variant="outlined"
        />
      </Box>

      {/* 상세 정보 */}
      {showDetails && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            현재: {currentShots.toLocaleString()} / 설계: {designLife.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            잔여: {remainingShots.toLocaleString()} 쇼트
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LifeCycleIndicator;
